import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

function Avatar({ opt, size = 20 }) {
  const url = opt?.avatar_url ? `${BACKEND}${opt.avatar_url}` : null;
  const style = { width: size, height: size };
  return url ? (
    <img src={url} alt={opt.name} className="rounded-full shrink-0 object-cover" style={{ ...style, border: "1px solid #EBE8E3" }} />
  ) : (
    <span className="rounded-full shrink-0 flex items-center justify-center font-semibold"
      style={{ ...style, fontSize: size * 0.42, background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3" }}>
      {initials(opt?.name) || "?"}
    </span>
  );
}

export function ResponsibleMultiSelect({ users, contactName, value = [], onChange, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const sortedUsers = [...users].sort((a, b) => (a.name || "").localeCompare(b.name || "", "da", { sensitivity: "base" }));
  const opts = [
    ...sortedUsers.map((u) => ({ token: u.id, name: u.name, avatar_url: u.avatar_url })),
    ...(contactName ? [{ token: "__contact__", name: `${contactName} (kunde)`, avatar_url: null, isContact: true }] : []),
  ];
  const selected = opts.filter((o) => value.includes(o.token));
  const toggle = (t) => onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`flex w-full items-center justify-between gap-2 rounded-xl border border-input bg-white px-2.5 text-sm ${className}`}
        >
          {selected.length === 0 ? (
            <span className="truncate text-muted-foreground">Vælg ansvarlige</span>
          ) : (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="flex -space-x-1.5 shrink-0">
                {selected.slice(0, 3).map((o) => (
                  <span key={o.token} className="ring-2 ring-white rounded-full"><Avatar opt={o} size={20} /></span>
                ))}
              </span>
              <span className="truncate text-[#1A1919]">
                {selected.length <= 2 ? selected.map((o) => o.name).join(", ") : `${selected.length} valgt`}
              </span>
            </span>
          )}
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5 rounded-xl" align="start">
        <div className="max-h-64 overflow-y-auto">
          {opts.length === 0 && <p className="px-2 py-1.5 text-sm text-muted-foreground">Ingen brugere</p>}
          {opts.map((o) => (
            <label
              key={o.token}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[#F3F1EC] cursor-pointer"
              data-testid={`${testid}-option-${o.token}`}
            >
              <Checkbox
                checked={value.includes(o.token)}
                onCheckedChange={() => toggle(o.token)}
                className="border-[#D6D3D1] data-[state=checked]:bg-[#A83232] data-[state=checked]:border-[#A83232]"
              />
              <Avatar opt={o} size={24} />
              <span className="text-sm truncate">{o.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
